import {memo, useEffect, useMemo, useState} from 'react';

import styled, {Keyframes} from 'styled-components';

import {
  DELIVERY_TRACKER_ANIMATION_SECONDS,
  useDeliveryTrackerAnimation,
} from '~/shared/hooks/useDeliveryTrackerAnimation';
import {getLocalizationService} from '~/shared/services/localisationService';

// for circumference calculation we use diameter of 95px minus stroke-width 4px x2
const TRACKER_CIRCUMFERENCE = (95 - (4 * 2)) * Math.PI;

const TrackerContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const TrackerBody = styled.div<{keyFrame: Keyframes | null}>`
  width: 95px;
  height: 95px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({theme}) => theme.zIndex.languageChangeButton};
  animation-name: ${({keyFrame}) => keyFrame || 'none'};
  animation-fill-mode: forwards;
  animation-duration: ${DELIVERY_TRACKER_ANIMATION_SECONDS}s;
`;

const CirclesSvgContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const CirclesSvg = styled.svg`
  transform: rotate(-90deg);
`;

const InnerCircle = styled.circle`
  stroke: ${(({theme}) => theme.dummyTracker.innerCircle.strokeColor)};
`;

const ProgressCircle = styled.circle`
  stroke: ${(({theme}) => theme.dummyTracker.progressCircle.strokeColor)};
  transition: stroke-dashoffset 0.5s linear 0.6s;
`;

// Time labels components

const TimeLabels = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  user-select: none;
  z-index: ${({theme}) => theme.zIndex.languageChangeButton};
`;

const MinutesLabel = styled.span`
  font-size: 20px;
  font-weight: 700;
`;

const TimeUnitLabel = styled.span`
  line-height: 19px;
  color: ${({theme}) => theme.dummyTracker.timeUnitLabel.color};
`;

type DeliveryTrackerViewProps = {
  className?: string;
  isOverTwoHours: boolean;
  minutesToRender: number | string | null;
  totalEta: number;
  showCircleProgress?: boolean;
  noCounterReplacementComponent?: React.ReactElement;
};

const DeliveryTrackerView = ({
  className,
  isOverTwoHours,
  minutesToRender,
  totalEta,
  showCircleProgress = true,
  noCounterReplacementComponent,
}: DeliveryTrackerViewProps) => {
  const {t} = getLocalizationService();
  const {keyFrame, activateAnimation} = useDeliveryTrackerAnimation();
  const [_showCircleProgress, _setShowCircleProgress] = useState(showCircleProgress);

  const offset = useMemo(() => {
    if (isOverTwoHours) {
      return 0;
    }

    // if the type is not a number, the circle should be 100% filled
    // offset is 0 in this case
    if (typeof minutesToRender !== 'number') {
      return 0;
    }

    const percent = ((minutesToRender * 100) / totalEta) / 100;
    return TRACKER_CIRCUMFERENCE - (percent * TRACKER_CIRCUMFERENCE);
  }, [minutesToRender, totalEta, isOverTwoHours]);

  useEffect(() => {
    if (showCircleProgress !== _showCircleProgress) {
      activateAnimation(() => _setShowCircleProgress(showCircleProgress));
    }
  }, [activateAnimation, showCircleProgress, _showCircleProgress]);

  return (
    <TrackerContainer className={className}>
      <TrackerBody keyFrame={keyFrame}>
        <CirclesSvgContainer>
          {_showCircleProgress ? (
            <CirclesSvg width={95} height={95}>
              <InnerCircle strokeWidth={4} fill="white" r={43.5} cx={47.5} cy={47.5} />
              <ProgressCircle
                strokeWidth={4}
                className="yellow-stroke"
                fill="transparent"
                r={43.5}
                cx={47.5}
                cy={47.5}
                strokeDasharray={`${TRACKER_CIRCUMFERENCE} ${TRACKER_CIRCUMFERENCE}`}
                strokeDashoffset={offset}
              />
            </CirclesSvg>
          ) : noCounterReplacementComponent}
        </CirclesSvgContainer>

        {_showCircleProgress && (
          <TimeLabels>
            <MinutesLabel>{minutesToRender}</MinutesLabel>
            {!isOverTwoHours && <TimeUnitLabel>{t('dummy_tracker_minutes_time_unit')}</TimeUnitLabel>}
          </TimeLabels>
        )}
      </TrackerBody>
    </TrackerContainer>
  );
};

export default memo(DeliveryTrackerView);
